/**
 * FacturaDigital v4 — Main application JS
 * Bootstrap 5 initialization, global helpers, plugin setup
 */

// Primero: Sentry instala sus manejadores globales antes de que corra el resto del bundle.
import './lib/sentry.js';
import '../scss/style.scss';
import './layout.js';
import './cuenta/pago.js';
import './cuenta/factura-compra.js';
import './configuracion/contacto-tecnico.js';   // Configuración › API y desarrolladores › Contacto técnico (spec 2026-10-29 §8)
import { fdFadeIn, fdFadeOut, fdSlideDown, fdSlideUp, fdPulse, fdScrollTo } from './lib/motion.js';
import * as CfdiRules from './lib/cfdi-rules.js';
import * as FdTasa from './lib/tasa.js';
import { initCfdiShare } from './lib/cfdi-share.js';
import * as SmtpDiag from './smtp/diagnostico.js';
import * as EstatusSat from './documentos/estatus-sat.js';
import * as CuentasBancarias from './pagos/cuentas-bancarias.js';
import * as Portapapeles from './lib/portapapeles.js';
import * as Apariencia from './apariencia.js';
import * as Portales from './configuracion/portales.js';
import * as PlantillasIA from './configuracion/plantillas-ia.js';
import * as SucursalesUsuario from './configuracion/sucursales-usuario.js';
import * as DocumentosPrevios from './documentos/previos.js';
import * as Limites from './lib/limite-complementos.js';
import * as Pestanas from './documentos/pestanas.js';
import * as Conceptos from './documentos/conceptos.js';
import * as Relaciones from './documentos/relaciones.js';
import * as Filas from './documentos/filas.js';
import * as SatdocSeguimiento from './satdocumentos/seguimiento.js';
import * as MigracionReglas from './migracion/reglas.js';
import { montarOrbe as montarOrbeMigracion } from './migracion/orbe-migracion.js';
import { iniciarPildora as iniciarPildoraMigracion } from './migracion/pildora.js';
import { crearMotor as crearMotorOrbe } from './lib/orbe-red.js';
import { initTypeaheadEnModales } from './typeahead-modal.js';
import tippy from 'tippy.js';
import { opcionesTooltipBs } from './lib/tooltip-tippy.js';
import 'tippy.js/dist/tippy.css';
import * as accounting from 'accounting-js';
import NProgress from 'nprogress';

window.fdFadeIn   = fdFadeIn;
window.fdFadeOut  = fdFadeOut;
window.fdSlideDown = fdSlideDown;
window.fdSlideUp  = fdSlideUp;
window.fdPulse    = fdPulse;
window.fdScrollTo = fdScrollTo;
window.CfdiRules  = CfdiRules;
// Tipo de cambio escrito por el usuario (coma decimal, miles, ambigüedad): las vistas con
// script en línea (Emitidos › Aplicar pago, Timbrado masivo y por referencia) lo leen igual
// que Crear y que el servidor (libraries/Tasa_capturada.php).
window.FdTasa = FdTasa;
window.SmtpDiag   = SmtpDiag;
window.EstatusSat = EstatusSat;
window.CuentasBancarias = CuentasBancarias;
window.FDPortapapeles = Portapapeles;
window.FDApariencia = Apariencia;
window.FDPortales = Portales;
window.FDPlantillasIA = PlantillasIA;
window.FDSucursalesUsuario = SucursalesUsuario;   // Configuración › Usuarios › Sucursales del usuario (20-oct-2026)
window.FDDocumentosPrevios = DocumentosPrevios;
// Límite mensual de complementos (spec 2026-10-17): el menú y el timbrado de Crear (bundle cfdi-crear) lo leen de aquí.
window.FDLimites = Limites;
// Emitidos y Recibidos con pestañas (spec 2026-10-11): reglas puras que montan noble/documentos/listado.php y
// listado_recibidos.php (su JS en línea no pasa por Vite).
window.FDPestanas = Pestanas;
window.FDConceptos = Conceptos;
window.FDRelaciones = Relaciones;
window.FDFilas = Filas;   // filas de Emitidos que se abren (pulido 2026-10-12)
// Documentos del SAT: el seguimiento de una descarga (al cargar, al volver a la pestaña y al cambiar de documento) que
// monta noble/satdocumentos/index.php (su JS en línea no pasa por Vite).
window.FDSatdocSeguimiento = SatdocSeguimiento;
// Asistente de migración (plan 2026-10-20, Task 11): las reglas puras y la esfera de cristal que monta
// noble/migracion/index.php (su JS en línea no pasa por Vite); Task 12: la píldora de la barra que monta
// noble/layout/_fd_migracion.php en todas las pantallas.
window.FDMigracion = { reglas: MigracionReglas, montarOrbe: montarOrbeMigracion, iniciarPildora: iniciarPildoraMigracion };
// El motor del orbe neuronal (lib/orbe-red.js) vive solo en este bundle: el orbe de Timbrando (cfdi-crear) lo toma de
// aquí. Si los dos bundles lo importaran, Vite lo partiría en un chunk con `import` que un <script> clásico no lee.
window.FDOrbeRed = { crearMotor: crearMotorOrbe };
window.tippy      = tippy;
window.accounting = accounting;

(function ($) {
  'use strict';

  // -------------------------------------------------------------------
  // Reducir movimiento: con prefers-reduced-motion las animaciones de
  // jQuery (fadeIn, slideDown, el animate del scroll en Pagos, Carta Porte
  // y el timbrado) terminan al instante con su estado final y su callback.
  // Sigue a la preferencia si cambia con la página abierta.
  // -------------------------------------------------------------------
  if (window.matchMedia) {
    var mqMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');
    var aplicarMovimiento = function () { $.fx.off = mqMovimiento.matches; };
    aplicarMovimiento();
    if (mqMovimiento.addEventListener) mqMovimiento.addEventListener('change', aplicarMovimiento);
    else if (mqMovimiento.addListener) mqMovimiento.addListener(aplicarMovimiento);
  }

  // -------------------------------------------------------------------
  // jQuery ↔ Bootstrap 5 bridge: allows $().modal(), $().tooltip(), etc.
  // This preserves backward compatibility with existing view code.
  // -------------------------------------------------------------------
  $.fn.modal = function (action) {
    return this.each(function () {
      var inst = bootstrap.Modal.getInstance(this) || new bootstrap.Modal(this);
      if (action === 'show') inst.show();
      else if (action === 'hide') inst.hide();
      else if (action === 'toggle') inst.toggle();
      else if (action === 'dispose') inst.dispose();
    });
  };

  $.fn.tooltip = function (action) {
    return this.each(function () {
      var inst = bootstrap.Tooltip.getInstance(this) || new bootstrap.Tooltip(this);
      if (action === 'show') inst.show();
      else if (action === 'hide') inst.hide();
      else if (action === 'dispose') inst.dispose();
    });
  };

  // Shim for bootstrap4-validator: returns jQuery chain so .on('submit') still works
  $.fn.validator = $.fn.validator || function () { return this; };

  $(function () {
    // -----------------------------------------------------------------
    // Tippy.js — replaces Bootstrap tooltips and CSS pseudo-element hints
    // -----------------------------------------------------------------

    // .fd-hint elements (info icons with data-hint attribute)
    tippy('.fd-hint[data-hint]', {
      content: function (el) { return el.getAttribute('data-hint'); },
      placement: 'top',
      arrow: true,
      delay: [200, 0],
      maxWidth: 260,
      theme: 'fd'
    });

    // Bootstrap-style [data-bs-toggle="tooltip"] — delegate to Tippy
    tippy('[data-bs-toggle="tooltip"]', opcionesTooltipBs(function () { return window.bootstrap; }));

    // Legacy [data-toggle="tooltip"] (pre-BS5)
    tippy('[data-toggle="tooltip"]', {
      content: function (el) {
        return el.getAttribute('data-original-title') || el.getAttribute('title') || '';
      },
      arrow: true,
      delay: [150, 0],
      theme: 'fd'
    });

    // Popovers (opt-in via data-bs-toggle="popover")
    document.querySelectorAll('[data-bs-toggle="popover"]').forEach(function (el) {
      new bootstrap.Popover(el);
    });

    // -----------------------------------------------------------------
    // Select2 auto-initialization on .select2
    // -----------------------------------------------------------------
    if ($.fn.select2) {
      $('.select2').select2({
        theme: 'bootstrap-5',
        width: '100%'
      });
    }

    // -----------------------------------------------------------------
    // Bootbox locale
    // -----------------------------------------------------------------
    if (typeof bootbox !== 'undefined') {
      bootbox.setDefaults({
        locale: 'es',
        centerVertical: true
      });
    }

    // -----------------------------------------------------------------
    // NProgress — barra slim. NUNCA esperar window.load (fuentes/woff2
    // de Tabler ~460KB + 8 faces: en iPhone/Tailscale eso deja Safari
    // en “pensando” aunque el HTML ya llegó).
    NProgress.configure({
      showSpinner: false,
      trickleSpeed: 200,
      minimum: 0.12,
      parent: 'body'
    });
    NProgress.start();
    var npDone = function () {
      try { NProgress.done(); } catch (e) { /* noop */ }
    };
    if (document.readyState === 'complete') {
      npDone();
    } else {
      $(function () { npDone(); });
      $(window).on('load', npDone);
      setTimeout(npDone, 1500);
    }
    // AJAX: solo mostrar barra si tarda >400ms (evita flicker y no se
    // queda pegada si un request no dispara ajaxStop).
    var npAjaxTimer = null;
    $(document).ajaxStart(function () {
      clearTimeout(npAjaxTimer);
      npAjaxTimer = setTimeout(function () { NProgress.start(); }, 400);
    });
    $(document).ajaxStop(function () {
      clearTimeout(npAjaxTimer);
      npDone();
    });
    window.NProgress = NProgress;

    // -----------------------------------------------------------------
    // Global SweetAlert2 helpers
    // -----------------------------------------------------------------
    window.swalSuccess = function (message, intTimer, bitToast) {
      if (intTimer === undefined) intTimer = 7000;
      if (bitToast === undefined) bitToast = true;

      // La columna oscura de 360px x 100vh (feedback 2026-09-01) no la pinta
      // SweetAlert2 sino _fd-modales.scss, sobre .swal2-container.swal2-backdrop-show;
      // ese mismo archivo ya la apaga en los toasts con body.swal2-toast-shown, que
      // es más específico. Así que un toast NO necesita pasar `backdrop`, y si lo
      // pasa swal2 lo rechaza y lo avisa en consola: 'The parameter "backdrop" is
      // incompatible with toasts'.
      // El modal bloqueante (bitToast=false: "Validando documentos... Espere" de
      // crear_facturamanual.php) sí conserva su velo y lo pide explícito en true:
      // `undefined` NO era el default, pisaba el true de swal2 y le ponía
      // swal2-no-backdrop al body, dejando al modal sin velo (medido 2026-09-22).
      var params = {
        toast: bitToast,
        position: 'top-end',
        showConfirmButton: false,
        timer: intTimer,
        timerProgressBar: true,
        customClass: { title: 'text-success' }
      };
      if (!bitToast) params.backdrop = true;

      var Toast = Swal.mixin(params);

      Toast.fire({ icon: 'success', title: message });
    };

    window.swalSuccessBox = function (message, title) {
      if (title === undefined) title = 'Listo';
      Swal.fire({
        icon: 'success',
        title: title,
        html: message,
        showCloseButton: true,
        confirmButtonText: '<i class="ti ti-thumb-up me-1"></i> Entendido'
      });
    };

    window.swalError = function (message) {
      var Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        // Sin `backdrop`: swalError siempre es toast y el velo ya lo apaga
        // _fd-modales.scss (body.swal2-toast-shown). Ver swalSuccess, arriba.
        showConfirmButton: false,
        timer: 13000
      });
      // Tono sereno (docs/superpowers/guia-tono-mensajes.md, sección 2): ícono ámbar de aviso, nunca la X roja.
      Toast.fire({ icon: 'warning', title: message });
    };

    window.swalErrorBox = function (message, title) {
      if (title === undefined) title = 'Revisemos esto';
      // Los detalles se pueden COPIAR (pedido de soporte 2026-08-25): botón en el
      // footer + el modal no se cierra si el mouse sale del popup al seleccionar.
      // El copiado vive en lib/portapapeles.js y se publica como
      // window.FDPortapapeles para que cfdi/error-translator.js lo use sin
      // importarlo: app.js (bundle facturadigital) y error-translator (bundle
      // cfdi-crear) son entries <script> clásicos, y un módulo importado por los
      // dos generaría un chunk con import que truena (gotcha de catalogos-sat).
      // Ícono ámbar de aviso, nunca la X roja: lo que dice se puede resolver (guía de tono, sección 2).
      Swal.fire({
        icon: 'warning',
        title: title,
        html: message,
        footer: '<button type="button" class="btn btn-sm btn-outline-secondary fd-err-copy">' +
          '<i class="ti ti-copy me-1"></i>Copiar detalle</button>',
        showCloseButton: true,
        allowOutsideClick: false,
        confirmButtonText: '<i class="ti ti-thumb-up me-1"></i> Entendido',
        customClass: { popup: 'fd-err-popup' },
        didOpen: function (popup) {
          var btn = popup.querySelector('.fd-err-copy');
          if (!btn) return;
          btn.addEventListener('click', function () {
            var cont = popup.querySelector('.swal2-html-container');
            var cuerpo = (cont && cont.innerText) ? cont.innerText : String(message);
            var texto = Portapapeles.textoDeError({ titulo: title, mensaje: cuerpo });
            // El aviso va en el propio botón, NO con FD.toast: un Swal nuevo
            // encima reemplaza al que está abierto y se llevaría el mensaje.
            var listo = function (ok) {
              btn.innerHTML = ok ? '<i class="ti ti-check me-1"></i>Copiado'
                                 : '<i class="ti ti-info-circle me-1"></i>Selecciónalo y cópialo';
              setTimeout(function () {
                btn.innerHTML = '<i class="ti ti-copy me-1"></i>Copiar detalle';
              }, 2000);
            };
            // contenedor = popup: el <textarea> de respaldo tiene que colgar de
            // donde vive el foco, o el focus trap de Swal/Bootstrap lo deja sin
            // selección y execCommand('copy') falla.
            Portapapeles.copiarTexto(texto, { contenedor: popup }).then(listo, function () { listo(false); });
          });
        }
      });
    };

    // Helpers de "compartir CFDI" (WhatsApp + portapapeles) usados por el modal de
    // timbrado exitoso (bundle cfdi-crear) y por documentos/listado.php.
    // El Swal de "Timbrado exitoso" que vivía aquí lo sustituyó el modal propio
    // #modal-timbrado-exitoso (cfdi/timbrado-exitoso.js).
    initCfdiShare(window);

    // -----------------------------------------------------------------
    // PDF / XML download helpers
    // -----------------------------------------------------------------
    window.swalDescargarPDF = function (uuid) {
      var base = document.querySelector('meta[name="base-url"]');
      var baseUrl = base ? base.content : '/';
      var url = baseUrl + 'docs/pdf/' + uuid;
      window.open(url, '_blank').focus();
    };

    window.swalDescargarXML = function (rfcemisor, uuid) {
      var base = document.querySelector('meta[name="base-url"]');
      var baseUrl = base ? base.content : '/';
      var url = baseUrl + 'docs/xml/' + rfcemisor + '/' + uuid;
      window.open(url, '_blank').focus();
    };

    // -----------------------------------------------------------------
    // Email validation helper
    // -----------------------------------------------------------------
    window.validarEmails = function (emails) {
      var list = emails.split(',');
      var regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (var i = 0; i < list.length; i++) {
        if (!regex.test(list[i].trim())) return false;
      }
      return true;
    };

    // -----------------------------------------------------------------
    // Typeahead helpers
    // -----------------------------------------------------------------
    window.fixTypeaheadStyle = function () {
      $('.twitter-typeahead').css('width', '100%');
    };

    // Menús de typeahead dentro de modales con scroll: se sacan del recorte del modal-body.
    initTypeaheadEnModales();

    window.substringMatcher = function (strs) {
      return function findMatches(q, cb) {
        var matches = [];
        var substrRegex = new RegExp(q, 'i');
        $.each(strs, function (i, str) {
          if (substrRegex.test(str)) matches.push(str);
        });
        cb(matches);
      };
    };

    // -----------------------------------------------------------------
    // Kiosko icon redraw helper
    // -----------------------------------------------------------------
    window.swalRedrawIconsKiosko = function () {
      $('.estatus-envio-0').html('<span data-bs-toggle="tooltip" title="Enviar">Enviar</span>');
      $('.estatus-envio-1').html('<span data-bs-toggle="tooltip" title="Enviado. Haz clic para reenviarlo.">Enviado</span>');
      $('.estatus-envio-2').html('<span data-bs-toggle="tooltip" title="Mensaje leído por el destinatario.">Leído</span>');
      $('.estado-cfdi-0').html('<span data-bs-toggle="tooltip" title="Cancelado"><i class="ti ti-x"></i> <s>Cancelado</s></span>');
      $('.estado-cfdi-1').html('<span data-bs-toggle="tooltip" title="Cancelar CFDI en el SAT"><i class="ti ti-x"></i> Cancelar</span>');
    };

    // -----------------------------------------------------------------
    // Aspect ratio calculation helper
    // -----------------------------------------------------------------
    window.calculateAspectRatioFit = function (srcWidth, srcHeight, maxWidth, maxHeight) {
      var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
      return { width: srcWidth * ratio, height: srcHeight * ratio };
    };

    // -----------------------------------------------------------------
    // Traducción de errores del SAT: NO vive aquí. La única tabla es
    // cfdi/error-translator.js (bundle cfdi-crear), verificada contra la
    // matriz oficial del SAT. Aquí había una segunda (SAT_ERRORS +
    // window.traducirErrorTimbrado) que ninguna vista llamaba y que
    // redactaba distinto los mismos códigos; se retiró el 2026-09-28.
    // No la regreses: src/js/cfdi/__tests__/errores-sat-matriz.test.js
    // falla si aparece otra tabla de códigos SAT fuera del traductor.
    // -----------------------------------------------------------------
  });
})(jQuery);
